import { useCallback, useMemo, useState } from "react";
import _ from "lodash";

import theme from "theme";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import PageHeader from "components/PageHeader";
import Stack from "components/Stack";
import toast from "components/Toast";
import { useLoadingBackdrop } from "components/useLoadingBackdrop";

import { CompanyCustomFieldSyncingType } from "entities/Field/constants";
import AddLinkedFieldFilters from "entities/Superuser/components/AddLinkedFieldFilters";
import DealPackage from "entities/Superuser/components/DealPackage";
import {
  addLinkIdsToField,
  IDealLinkedPackage,
  IDealPackageSyncingType,
} from "entities/Superuser/sdk";

const LinkedFields = () => {
  const [showLoading, hideLoading] = useLoadingBackdrop();
  const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
  const [selectedLinkId, setSelectedLinkId] = useState<string>("");
  const [dealPackages, setDealPackages] = useState<Array<IDealLinkedPackage>>(
    []
  );

  const [selectedDealPackagesSyncingType, setSelectedDealPackagesSyncingType] =
    useState<{
      [dealId: number]:
        | CompanyCustomFieldSyncingType.ERASE_ALL_VALUES
        | CompanyCustomFieldSyncingType.SYNC_CURRENT_VALUE_TO_REST
        | CompanyCustomFieldSyncingType.SYNC_REST_VALUES_TO_CURRENT_VALUE;
    }>([]);

  const handleSelectSyncingOption = useCallback(
    ({ syncingType, dealId }) =>
      setSelectedDealPackagesSyncingType((prev) => ({
        ...prev,
        [dealId]: syncingType,
      })),
    []
  );

  const handleSave = useCallback(() => {
    if (!_.isNull(selectedFieldId) && !_.isEmpty(selectedLinkId)) {
      showLoading();

      const dealPackages: Array<IDealPackageSyncingType> = Object.entries(
        selectedDealPackagesSyncingType
      ).map(([deal_id, syncing_type]) => ({
        deal_id: Number(deal_id),
        syncing_type,
      }));

      addLinkIdsToField({
        fieldId: selectedFieldId,
        linkId: selectedLinkId,
        dealPackages,
      })
        .then(() => {
          toast.successMessage("Linked field has been added successfully!");
          setDealPackages([]);
          setSelectedDealPackagesSyncingType([]);
        })
        .catch(handleInvalidRequest)
        .finally(() => {
          hideLoading();
        });
    }
  }, [
    selectedDealPackagesSyncingType,
    selectedFieldId,
    selectedLinkId,
    showLoading,
    hideLoading,
  ]);

  const saveButtonIsDisabled = useMemo(
    () =>
      dealPackages
        .map((dealPackage) =>
          _.has(selectedDealPackagesSyncingType, dealPackage.deal_id)
        )
        .filter((item) => !item).length > 0,
    [dealPackages, selectedDealPackagesSyncingType]
  );

  const handleCheck = useCallback(({ dealLinkedPackages, fieldId, linkId }) => {
    setDealPackages(dealLinkedPackages);
    setSelectedFieldId(fieldId);
    setSelectedLinkId(linkId);
    setSelectedDealPackagesSyncingType([]);
  }, []);

  const handleRaiseError = useCallback(() => setDealPackages([]), []);

  return (
    <Stack
      sx={{
        padding: theme.spacing(4, 3, 0),
      }}
      spacing={3}
    >
      <PageHeader title="Add linked field" />
      <AddLinkedFieldFilters
        onCheck={handleCheck}
        onRaiseError={handleRaiseError}
      />
      <Stack spacing={3}>
        {!_.isNil(selectedFieldId) &&
          dealPackages.map((dealPackage) => (
            <DealPackage
              key={`${dealPackage.deal_id}-${selectedFieldId}-${selectedLinkId}`}
              dealPackage={dealPackage}
              currentFieldId={selectedFieldId}
              onSelectSyncingOption={({ syncingType }) =>
                handleSelectSyncingOption({
                  syncingType,
                  dealId: dealPackage.deal_id,
                })
              }
            />
          ))}
      </Stack>
      {!_.isEmpty(dealPackages) && (
        <Button
          sx={{ alignSelf: "flex-end" }}
          onClick={handleSave}
          disabled={saveButtonIsDisabled}
        >
          Save changes
        </Button>
      )}
    </Stack>
  );
};

export default LinkedFields;
